/**
 * Reads the first sheet of an .xlsx workbook or a .csv file into rows of cell text.
 * No third-party parser: .xlsx is a zip of XML parts, unpacked with the browser's
 * DecompressionStream and read with DOMParser.
 */

export type SheetRows = string[][];

export function parseCsv(text: string): SheetRows {
  const src = text.replace(/^\uFEFF/, '');
  const rows: SheetRows = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

type ZipEntry = { name: string; method: number; offset: number; size: number };

function zipEntries(buf: ArrayBuffer): ZipEntry[] {
  const view = new DataView(buf);
  let eocd = -1;
  for (let i = buf.byteLength - 22; i >= Math.max(0, buf.byteLength - 65557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('Not a valid .xlsx file.');
  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const decoder = new TextDecoder();
  const entries: ZipEntry[] = [];
  for (let n = 0; n < count; n++) {
    if (view.getUint32(p, true) !== 0x02014b50) break;
    const method = view.getUint16(p + 10, true);
    const size = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const local = view.getUint32(p + 42, true);
    const name = decoder.decode(new Uint8Array(buf, p + 46, nameLen));
    const dataStart = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    entries.push({ name, method, offset: dataStart, size });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

async function readEntry(buf: ArrayBuffer, e: ZipEntry): Promise<string> {
  const bytes = new Uint8Array(buf, e.offset, e.size);
  if (e.method === 0) return new TextDecoder().decode(bytes);
  if (e.method !== 8) throw new Error('Unsupported compression in .xlsx file.');
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('This browser cannot read .xlsx files. Save the sheet as CSV and upload that instead.');
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}

function colIndex(ref: string): number {
  const letters = ref.replace(/[^A-Z]/gi, '').toUpperCase();
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

export async function parseXlsx(buf: ArrayBuffer): Promise<SheetRows> {
  const entries = zipEntries(buf);
  const byName = new Map(entries.map((e) => [e.name.replace(/^\/+/, ''), e]));
  const xml = (s: string) => new DOMParser().parseFromString(s, 'application/xml');

  let sheetPath = 'xl/worksheets/sheet1.xml';
  const wb = byName.get('xl/workbook.xml');
  const rels = byName.get('xl/_rels/workbook.xml.rels');
  if (wb && rels) {
    const firstSheet = xml(await readEntry(buf, wb)).getElementsByTagName('sheet')[0];
    const rid = firstSheet?.getAttribute('r:id') || firstSheet?.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id');
    const rel = Array.from(xml(await readEntry(buf, rels)).getElementsByTagName('Relationship')).find((r) => r.getAttribute('Id') === rid);
    const target = rel?.getAttribute('Target');
    if (target) sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
  }
  const sheet = byName.get(sheetPath);
  if (!sheet) throw new Error('Could not find the first sheet in this workbook.');

  const shared: string[] = [];
  const ss = byName.get('xl/sharedStrings.xml');
  if (ss) {
    for (const si of Array.from(xml(await readEntry(buf, ss)).getElementsByTagName('si'))) {
      shared.push(Array.from(si.getElementsByTagName('t')).map((t) => t.textContent || '').join(''));
    }
  }

  const rows: SheetRows = [];
  for (const r of Array.from(xml(await readEntry(buf, sheet)).getElementsByTagName('row'))) {
    const out: string[] = [];
    let next = 0;
    for (const c of Array.from(r.getElementsByTagName('c'))) {
      const ref = c.getAttribute('r');
      const idx = ref ? colIndex(ref) : next;
      const type = c.getAttribute('t');
      const v = c.getElementsByTagName('v')[0]?.textContent ?? '';
      let text: string;
      if (type === 's') text = shared[Number(v)] ?? '';
      else if (type === 'inlineStr') text = Array.from(c.getElementsByTagName('t')).map((t) => t.textContent || '').join('');
      else if (type === 'b') text = v === '1' ? 'TRUE' : 'FALSE';
      else text = v;
      while (out.length < idx) out.push('');
      out[idx] = text;
      next = idx + 1;
    }
    rows.push(out);
  }
  return rows.filter((row) => row.some((c) => String(c).trim() !== ''));
}

export async function readSheetFile(file: File): Promise<SheetRows> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) return parseCsv(await file.text());
  if (name.endsWith('.xlsx')) return parseXlsx(await file.arrayBuffer());
  if (name.endsWith('.xls')) throw new Error('Old .xls files are not supported. In Excel, use Save As → Excel Workbook (.xlsx) or CSV.');
  throw new Error('Upload an .xlsx or .csv file.');
}
